import React, { useEffect, useMemo, useRef, useState } from "react";

import { CopilotWebChat } from "@supportbot/supportbot-copilot-webchat";
import { useSelector } from "react-redux";

import { RootState } from "../../store/store";
import { fetchChatbotToken } from "../../utils/fetchChatbotToken";
import { fetchDirectLineToken } from "../../utils/fetchDirectLineToken";
import { logger } from "../../utils/logger";

// This type needs to match what the bot expects in the userToken property.
// Which is defined in suppoortbot/copilot-webchat from Sonata. The have not exported a type for it, so we need to maintain it here.
type UserTokenObject = {
  ParentID: string;
  ParentFirstName: string;
  ParentLastName: string;
  LoggedInStatus: string;
  SessionID: string;
  SessionStatus: string;
  AccessToken: string;
};

interface ChatbotWidgetProps {
  onCloseReady?: (closeFn: () => void) => void;
}

const ChatbotWidget: React.FC<ChatbotWidgetProps> = ({ onCloseReady }) => {
  const enableChatbot = useSelector((state: RootState) => state.appConfigs.enableChatbot);
  const user = useSelector((state: any) => state.auth.user);
  const isChatbotWhitelisted = useSelector((state: any) => state.customer.customer?.isChatbotWhitelisted === true);
  const directlineTokenUrl = useSelector((state: RootState) => state.appConfigs.chatbotDirectlineTokenUrl);
  const showChatbot = enableChatbot || isChatbotWhitelisted;
  const [directLineToken, setDirectLineToken] = useState<string | null>(null);
  const [delegateToken, setDelegateToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const closeChatRef = useRef<(() => void) | null>(null);

  const userToken: UserTokenObject = useMemo(
    () => ({
      ParentID: user?.profile?.customer_account_id || "",
      ParentFirstName: user?.profile?.given_name || "",
      ParentLastName: user?.profile?.family_name || "",
      LoggedInStatus: user ? "LoggedIn" : "LoggedOut",
      SessionID: user?.profile?.sid || user?.session_state || "",
      SessionStatus: "Active",
      AccessToken: delegateToken || "",
    }),
    [
      user?.profile?.customer_account_id,
      user?.profile?.given_name,
      user?.profile?.family_name,
      user?.profile?.sid,
      user?.session_state,
      user,
      delegateToken,
    ]
  );

  useEffect(() => {
    // Clear any stale webchat session state from a previous page load.
    // CopilotWebChat (botframework-webchat) persists conversation IDs in
    // sessionStorage, which causes initialisation errors when a new Direct Line
    // token is fetched for a conversation that no longer exists.
    const keysToRemove = Object.keys(sessionStorage).filter((k) => /botframework|directline|webchat|copilot/i.test(k));
    keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    logger.debug("[Chatbot] Cleared stale webchat sessionStorage keys:", keysToRemove);
  }, []);

  useEffect(() => {
    // Only initialize if feature is enabled or user is whitelisted
    if (!showChatbot) {
      return;
    }

    // Need user access token to exchange for delegate token
    if (!user || !user.access_token) {
      setIsLoading(false);
      return;
    }

    // Wait until appConfigs have loaded and provided the Direct Line URL
    if (!directlineTokenUrl) {
      return;
    }

    logger.debug("[Chatbot] Initializing widget...");

    // Fetch both tokens in parallel
    Promise.all([fetchDirectLineToken(directlineTokenUrl), fetchChatbotToken(user.access_token)])
      .then(([dlToken, chatbotToken]) => {
        if (dlToken) {
          logger.info("[Chatbot] Direct Line token received");
          setDirectLineToken(dlToken);
        } else {
          logger.error("[Chatbot] Failed to get Direct Line token");
          setError("Failed to connect to chatbot service.");
        }

        if (chatbotToken) {
          logger.info("[Chatbot] Delegate token received");
          setDelegateToken(chatbotToken);
        } else {
          logger.error("[Chatbot] Failed to get delegate token");
          setError("Failed to authenticate with chatbot service.");
        }

        setIsLoading(false);
      })
      .catch((err) => {
        logger.error("[Chatbot] Error initializing:", err);
        setError("Failed to initialize chatbot");
        setIsLoading(false);
      });
  }, [showChatbot, user?.access_token, directlineTokenUrl]);

  // Guard: Don't render if feature is disabled and user is not whitelisted (after all hooks are called)
  if (!showChatbot) {
    return null;
  }

  if (isLoading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Loading chatbot...</p>
      </div>
    );
  }

  if (error || !directLineToken || !delegateToken) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "#d32f2f" }}>
        <p>
          <strong>Chatbot Unavailable</strong>
        </p>
        <p style={{ fontSize: "14px", marginTop: "10px" }}>{error || "Unable to connect to the chatbot service."}</p>
        <p style={{ fontSize: "12px", marginTop: "10px", color: "#666" }}>
          Please contact support if this issue persists.
        </p>
      </div>
    );
  }

  if (!user || !user.access_token) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>Please log in to use the chatbot</p>
      </div>
    );
  }

  logger.debug("[Chatbot] Rendering CopilotWebChat component");

  logger.debug("[Chatbot] User context being passed to bot:");
  logger.debug(user.access_token);

  logger.debug("[Chatbot] Direct Line token being passed to bot:");
  logger.debug(directLineToken);

  logger.debug("[Chatbot] Delegate token being passed to bot:");
  logger.debug(delegateToken);

  logger.debug("[Chatbot] UserToken JSON being passed to bot:");
  logger.debug(JSON.stringify(userToken));

  return (
    <CopilotWebChat
      key={directLineToken}
      tokenEndpointUrl={directlineTokenUrl}
      initialToken={directLineToken}
      width="100%"
      height="calc(100% - 40px)"
      accentColor="#5ce4bb"
      autoStart={true}
      userToken={userToken} // User context and delegate token
      onCloseReady={(fn: () => void) => {
        closeChatRef.current = fn;
        if (onCloseReady) onCloseReady(fn);
      }}
    />
  );
};

export default ChatbotWidget;
